/**
 * BookAnExpert Component
 * Section showing a carousel of expert/coach cards
 */

import { ArrowRight } from "lucide-react"
import { memo } from "react"
import { Link } from "react-router-dom"
import { Carousel } from "@/components/carousel/Carousel"
import { useTranslation } from "@/language"
import type { Expert } from "../types/connect.types"
import { ExpertCard } from "./ExpertCard"

interface BookAnExpertProps {
  experts: Expert[]
  onFollow?: (userId: string) => void
  onUnfollow?: (userId: string) => void
}

export const BookAnExpert = memo(function BookAnExpert({
  experts,
  onFollow,
  onUnfollow,
}: BookAnExpertProps) {
  const { t } = useTranslation("common")
  if (experts.length === 0) return null

  return (
    <section className="mb-10 md:py-10 max-w-360 mx-auto px-5 md:px-17.5">
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div>
          <h2 className="uppercase text-xl font-semibold md:section-heading text-text-primary">
            {t("connect.bookAnExpert")}
          </h2>
          <p className="text-[14px] leading-[20px] text-text-priamry mt-2">
            {t("connect.bookAnExpertSubtitle")}
          </p>
        </div>

        <div className="mt-5">
          <Link
            to="/connect/experts"
            className="flex items-center gap-1.5 text-[14px] leading-[20px] text-text-primary transition-colors whitespace-nowrap font-semibold hidden md:flex"
          >
            {t("connect.findExperts")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Carousel */}
      <div className="mt-6">
        <Carousel
          data={experts}
          renderItem={expert => (
            <ExpertCard
              expert={expert}
              onFollow={onFollow}
              onUnfollow={onUnfollow}
              className="md:w-[520px] h-[342px]"
            />
          )}
          loop={false}
          itemWidth="min(520px, 100%)"
          contentClassName="gap-5"
          // showArrows
        />
      </div>

      <div className="mt-5">
        <Link
          to="/connect/experts"
          className="md:hidden flex items-center gap-1.5 text-[14px] leading-[20px] text-text-primary transition-colors whitespace-nowrap font-semibold"
        >
          {t("connect.findExperts")} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  )
})
